'use client';

import { useProductDetailModal as useProductDetailModalBase } from '@hyphengroup/cms-components/hooks/useProductDetailModal';
import type { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import type { RenderPdpModalHero } from '@hyphengroup/cms-components/types/cmsTypes';
import classNames from 'classnames';
import { useCallback } from 'react';
import { usePageDataContextOptional } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import type { PdpModalPageConfig } from '$/(cms)/_modules/cmsService/types';
import { EnumFlexibleHeroBlockHeroType } from '$/(cms)/_modules/cmsService/types';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import { FlexibleHeroBlock } from '$/components/cms/components-user/FlexibleHeroBlock/FlexibleHeroBlock';
import { getProductHeroCtaLinkOverride } from '$/components/cms/components-user/FlexibleHeroBlock/utils';
import {
  getPageProductMetaConfig,
  getProductMetaConfigCtaLinkOverride,
} from '$/components/cms/helpers/productMetaConfig';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import type { PageContentContainerType } from '$/components/PageContentContainer';
import { useClientContext } from '$/modules/context/client';

/**
 * Hero body wrapper for the modal. On a page the hero text gets its gutters
 * from `PageContentContainer` (section padding + centred max-width); inside
 * the modal only the horizontal padding applies, matched to the modal's own
 * section padding (`px-padding/x`) — no page-level max-width or centring.
 */
const PdpModalHeroContainer: PageContentContainerType = ({
  children,
  className,
  ...other
}) => (
  <div
    className={classNames(
      'w-full px-spacing/gap/section-x py-spacing/gap/section-y md:py-spacing/gap/section-y-lg',
      className,
    )}
    {...other}
  >
    {children}
  </div>
);

/**
 * The `#usePDP` config fetcher + the preview flag its cache bypass keys off
 * (CMS-1924). Supplied as props, the same way `getProductAction` is supplied
 * to `ProductBlockAsideModal` — app-provided fetchers are not
 * `CmsClientContext` material. For components that render
 * `ProductDetailsModalMode` outside the hook below: CardListing's product
 * cards (via `CardListingWrapper`) and MODAL-mode `ProductDetails` blocks
 * (via `ProductDetailsBlock`).
 *
 * Works with and without a `PageDataProvider` (apply-v3 has none — there
 * `isPreview` is simply false, matching its non-draft product fetches).
 */
export const usePdpModalConfigProps = () => {
  const { lang } = useClientContext();
  const isPreview = usePageDataContextOptional()?.isPreview ?? false;

  // The hero's authored Product CTA rides along as `heroCtaLinkOverride`: the hero is app-side, and
  // the modal's sticky footer has to go where the hero goes (CMS-2043).
  const getPdpModalConfigAction = useCallback(
    async (urlAlias: string) => {
      const config = await feApiFrontClient.getPdpModalConfig({
        urlAlias,
        lang,
        isDraft: isPreview,
      });
      return (
        config && {
          ...config,
          // The page found by the product's url_alias is that product's PDP, so the hero's type
          // is the only thing to check.
          heroCtaLinkOverride: getProductHeroCtaLinkOverride(config.hero),
        }
      );
    },
    [lang, isPreview],
  );

  // The hero component and its domain type are app-side, so the modal only
  // exposes a slot; this renderer narrows the fetched config back to the
  // widened shape the action above actually returns (CMS-1924 scope change).
  // Never a second <h1> in either branch: the listing page owns it (ticket
  // §6c) — the hero's headline renders as a plain element inside the modal.
  const renderPdpModalHero = useCallback<RenderPdpModalHero>(
    ({ config, product, ctaLinkOverride }) => {
      const hero = (config as PdpModalPageConfig | undefined)?.hero;
      if (hero) {
        return (
          <FlexibleHeroBlock
            {...hero}
            ctaLinkOverride={ctaLinkOverride}
            // The breadcrumb and the "Updated" date come from the page data in
            // context, which is the listing page's here, not the PDP's.
            showBreadcrumbs={false}
            showTimestamp={false}
            ContentContainer={PdpModalHeroContainer}
            isPageHeadline={false}
            wideLayoutBreakpoint="lg"
          />
        );
      }
      // No resolvable page config / no hero on the page → the same fallback
      // spirit as Benefits: derive the hero from the product itself. The
      // Product hero type builds its whole content (headline, key stats,
      // payment networks) from product attributes; component fields are only
      // authored overrides, so none are needed here.
      return (
        <FlexibleHeroBlock
          __typename="ComponentPagesComponentFlexibleHeroBlockComponent"
          id={`pdp-modal-product-hero-${product.uid ?? ''}`}
          heroType={EnumFlexibleHeroBlockHeroType.Product}
          product={product}
          ctaLinkOverride={ctaLinkOverride}
          ContentContainer={PdpModalHeroContainer}
          isPageHeadline={false}
          wideLayoutBreakpoint="lg"
        />
      );
    },
    [],
  );

  // The modal's own events (sticky nav, sticky footer Apply) need the sink too: callers that open
  // it from a listing have no other way to hand it over (CMS-2043).
  return {
    getPdpModalConfigAction,
    isPreview,
    renderPdpModalHero,
    pushDataLayer,
  };
};

/**
 * App wrapper over the cms-components `useProductDetailModal` — injects the
 * props above. fe entry points import THIS hook, not the cms-components one
 * directly (else the marker modal silently loses its benefit filter).
 */
export const useProductDetailModal = (props: {
  componentUniqueKey: string;
}) => {
  const modal = useProductDetailModalBase({
    ...props,
    ...usePdpModalConfigProps(),
  });
  const { openProductDetail: openWithLink } = modal;
  const productMetaConfig = getPageProductMetaConfig(
    usePageDataContextOptional()?.pageData.page.components,
  );

  // Every opener passes the page's Product Meta Config link for the product, so the modal's hero
  // and sticky footer go where the opening CTA went (CMS-2043).
  const openProductDetail = useCallback(
    (product: CmsProduct, idScrollTo: string | null = null) =>
      openWithLink(product, idScrollTo, {
        ctaLinkOverride: getProductMetaConfigCtaLinkOverride(
          productMetaConfig,
          product,
        ),
      }),
    [openWithLink, productMetaConfig],
  );

  return { ...modal, openProductDetail };
};
